import type { Monaco } from "@monaco-editor/react";
import type { GetProjectFilesResponseBody } from "@imagine/ai-service/shared-types/route-types";

export function pickDefaultFile(
  files: Array<GetProjectFilesResponseBody["files"][number]>,
): string | null {
  if (!files.length) return null;

  const editableFiles = files.filter((file) => !file.readOnly);
  const candidates = editableFiles.length > 0 ? editableFiles : files;

  const isCodeFile = (p: string) => /\.(tsx|ts|jsx|js)$/.test(p);

  const exactPatterns = [
    /^src\/router\.(tsx|ts|jsx|js)$/,
    /^src\/main\.(tsx|ts|jsx|js)$/,
    /^src\/index\.(tsx|ts|jsx|js)$/,
    /^src\/App\.(tsx|ts|jsx|js)$/,
    /^src\/pages\/index\.(tsx|ts|jsx|js)$/,
    /^app\/page\.(tsx|ts|jsx|js)$/,
    /^pages\/index\.(tsx|ts|jsx|js)$/,
  ];
  for (const pattern of exactPatterns) {
    const match = candidates.find((f) => pattern.test(f.path));
    if (match) return match.path;
  }

  const srcCode = candidates.filter(
    (f) => f.path.startsWith("src/") && isCodeFile(f.path),
  );

  const priorityNames = ["main", "App", "index", "page"];
  for (const name of priorityNames) {
    const regex = new RegExp(`/` + name + `[.](tsx|ts|jsx|js)$`);
    const candidate = srcCode.find((f) => regex.test(f.path));
    if (candidate) return candidate.path;
  }

  if (srcCode[0]) return srcCode[0].path;

  const anyCode = candidates.find((f) => isCodeFile(f.path));
  if (anyCode) return anyCode.path;

  return candidates[0]?.path ?? null;
}

export function inferLanguage(path: string | null): string {
  if (!path) return "plaintext";
  const extension = path.split(".").pop();

  switch (extension) {
    case "js":
    case "cjs":
    case "mjs":
    case "jsx":
      return "javascript";
    case "ts":
    case "tsx":
      return "typescript";
    case "json":
      return "json";
    case "css":
      return "css";
    case "scss":
    case "sass":
      return "scss";
    case "html":
    case "htm":
      return "html";
    case "md":
      return "markdown";
    case "py":
      return "python";
    case "yml":
    case "yaml":
      return "yaml";
    case "sh":
      return "shell";
    case "svg":
      return "xml";
    default:
      return "plaintext";
  }
}

export function createMonacoOptions({
  isReadOnly,
  isSvg,
}: {
  isReadOnly: boolean;
  isSvg: boolean;
}) {
  return {
    readOnly: isReadOnly,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 13,
    automaticLayout: true,
    padding: { top: 12, bottom: 96 },
    renderValidationDecorations: "off" as const,
    wordWrap: isSvg ? ("on" as const) : ("off" as const),
    wordWrapColumn: isSvg ? 120 : 0,
    stopRenderingLineAfter: isSvg ? 100000 : 10000,
  };
}

export function defineImagineThemes(monaco: Monaco) {
  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: true,
  });
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: true,
  });
  monaco.editor.defineTheme("imagine-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "variable", foreground: "2d2d31" },
      { token: "property", foreground: "2d2d31" },
      { token: "parameter", foreground: "2d2d31" },
      { token: "keyword", foreground: "cc0001", fontStyle: "" },
      { token: "number", foreground: "007fbf" },
      { token: "type", foreground: "007fbf" },
      { token: "class", foreground: "007fbf" },
      { token: "constant", foreground: "007fbf" },
      { token: "enum", foreground: "007fbf" },
      { token: "enumMember", foreground: "007fbf" },
      { token: "function", foreground: "8040ff" },
      { token: "method", foreground: "8040ff" },
      { token: "string", foreground: "2d2d31" },
      { token: "operator", foreground: "cc0001" },
      { token: "comment", foreground: "97979b" },
    ],
    colors: {
      "editor.foreground": "#29343d",
    },
  });

  monaco.editor.defineTheme("imagine-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "variable", foreground: "ededf0" },
      { token: "property", foreground: "ededf0" },
      { token: "parameter", foreground: "ededf0" },
      { token: "keyword", foreground: "ff6667", fontStyle: "" },
      { token: "number", foreground: "8cd9ff" },
      { token: "type", foreground: "8cd9ff" },
      { token: "class", foreground: "8cd9ff" },
      { token: "constant", foreground: "8cd9ff" },
      { token: "enum", foreground: "8cd9ff" },
      { token: "enumMember", foreground: "8cd9ff" },
      { token: "function", foreground: "b38cff" },
      { token: "method", foreground: "b38cff" },
      { token: "string", foreground: "ededf0" },
      { token: "operator", foreground: "ff6667" },
      { token: "comment", foreground: "818186" },
    ],
    colors: {
      "editor.foreground": "#d4d4d4",
    },
  });
}
